/**
 * EligibilitySection Component
 * Compact card grid for eligibility knockout rules.
 * Each rule: toggle, description, threshold pill (numeric) or rejection text (boolean).
 * Info button opens a modal explaining how the rule is calculated.
 */

import { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ShieldAlert, Save, Ban, Info } from 'lucide-react';
import { ConfirmSaveModal } from '../ConfirmSaveModal';
import type { EligibilityRuleItem } from '@/types/policy.types';

interface EligibilitySectionProps {
  data: EligibilityRuleItem[] | null;
  canEdit: boolean;
  onSave: (data: EligibilityRuleItem[]) => void;
  isSaving: boolean;
}

const RULE_DESCRIPTIONS: Record<string, string> = {
  regular_income: 'Debe tener al menos una fuente de ingreso regular detectada (deposito directo semanal, quincenal o mensual).',
  min_income: 'Ingreso mensual en MXN debe superar un umbral minimo para cubrir las cuotas semanales.',
  recent_deposit: 'Debe existir al menos un deposito reciente. Sin actividad reciente, la situacion financiera puede haber cambiado.',
  min_score: 'El score crediticio total debe superar un minimo para calificar a cualquier nivel.',
  min_seniority: 'Antiguedad minima en la plataforma para tener suficiente historial transaccional.',
  no_active_credit: 'Solo se permite un credito a la vez para controlar riesgo de sobreendeudamiento.',
};

const THRESHOLD_CONFIG: Record<string, { label: string; unit: string }> = {
  min_income: { label: 'Minimo', unit: 'MXN' },
  recent_deposit: { label: 'Maximo', unit: 'dias' },
  min_score: { label: 'Minimo', unit: 'pts' },
  min_seniority: { label: 'Minimo', unit: 'meses' },
};

const EDITABLE_RULES = new Set(Object.keys(THRESHOLD_CONFIG));

// ─── Detailed explanations for the info modal ───────────────────────────────

/** Step in a visual flow */
interface FlowStep {
  icon: string;
  text: string;
}

interface RuleDetail {
  title: string;
  /** Short summary at the top */
  summary: string;
  /** Visual numbered steps */
  steps: FlowStep[];
  /** Formula / key condition — shown in highlighted box */
  formula: string;
  /** Two examples: pass and fail */
  examples: { pass: string; fail: string };
  /** What the operator can change */
  configTip?: string;
}

const RULE_DETAILS: Record<string, RuleDetail> = {
  regular_income: {
    title: 'Ingreso regular',
    summary: 'Verifica que el cliente tenga al menos una fuente de ingreso estable y recurrente.',
    steps: [
      { icon: '1', text: 'Cada lunes se analizan depositos directos de los ultimos 90 dias' },
      { icon: '2', text: 'Se agrupan por patron (monto similar + frecuencia regular) → fuentes de ingreso' },
      { icon: '3', text: 'Cada fuente se clasifica: PRIMARY si es regular, frecuente y representa ≥40% del ingreso' },
      { icon: '4', text: 'Si tiene al menos 1 fuente PRIMARY → pasa la regla' },
    ],
    formula: '¿primary_sources ≥ 1?',
    examples: {
      pass: 'Deposito quincenal ~$800 USD, 14 depositos en 90 dias, 93% del ingreso → 1 fuente PRIMARY',
      fail: 'Solo 2 depositos esporadicos en 90 dias → 0 fuentes PRIMARY',
    },
  },
  min_income: {
    title: 'Ingreso minimo',
    summary: 'El ingreso mensual estimado debe superar un umbral para poder cubrir cuotas semanales.',
    steps: [
      { icon: '1', text: 'Se toman las fuentes de ingreso detectadas (Proceso 1)' },
      { icon: '2', text: 'Se calcula un monto confiable por fuente: penaliza ingresos muy variables' },
      { icon: '3', text: 'Se suma y divide entre 3 meses → ingreso mensual en USD' },
      { icon: '4', text: 'Se convierte a MXN con tipo de cambio vigente' },
    ],
    formula: '¿monthly_income_mxn ≥ umbral?',
    examples: {
      pass: 'Ingreso mensual $18,500 MXN → supera umbral de $12,000',
      fail: 'Ingreso mensual $9,200 MXN → por debajo del umbral',
    },
    configTip: 'El umbral en MXN es el valor que puedes modificar en la tarjeta.',
  },
  recent_deposit: {
    title: 'Deposito reciente',
    summary: 'El cliente debe tener actividad financiera reciente para que su perfil sea confiable.',
    steps: [
      { icon: '1', text: 'Se busca la fecha del ultimo deposito directo completado' },
      { icon: '2', text: 'Se calcula cuantos dias han pasado desde entonces' },
    ],
    formula: '¿dias_sin_deposito ≤ umbral?',
    examples: {
      pass: 'Ultimo deposito hace 12 dias → dentro del limite de 45',
      fail: 'Ultimo deposito hace 60 dias → fuera del limite',
    },
    configTip: 'El maximo de dias sin deposito es el valor que puedes modificar.',
  },
  min_score: {
    title: 'Score minimo',
    summary: 'El score crediticio total debe superar un piso para calificar a cualquier nivel.',
    steps: [
      { icon: '1', text: 'Se calculan 5 componentes: estabilidad, capacidad de pago, recargas MXN, antiguedad, nivel de ingreso' },
      { icon: '2', text: 'Cada componente da 0-100 puntos' },
      { icon: '3', text: 'Se ponderan con los pesos de la tab Scoring → score total 0-100' },
    ],
    formula: '¿score_total ≥ umbral?',
    examples: {
      pass: 'Score 62 pts → supera umbral de 40 → califica para BRONZE',
      fail: 'Score 28 pts → por debajo del umbral → no califica',
    },
    configTip: 'El score minimo es el valor que puedes modificar. Los pesos se ajustan en la tab Scoring.',
  },
  min_seniority: {
    title: 'Antiguedad minima',
    summary: 'Se necesita suficiente historial en la plataforma para evaluar el comportamiento financiero.',
    steps: [
      { icon: '1', text: 'Se toma la fecha de registro del cliente' },
      { icon: '2', text: 'Se calcula la diferencia en meses contra hoy' },
    ],
    formula: '¿months_in_platform ≥ umbral?',
    examples: {
      pass: 'Registrado hace 3 meses → supera umbral de 1 mes',
      fail: 'Registrado hace 2 semanas → no cumple el minimo',
    },
    configTip: 'La antiguedad minima en meses es el valor que puedes modificar.',
  },
  no_active_credit: {
    title: 'Sin credito activo',
    summary: 'Solo se permite un credito a la vez para controlar riesgo de sobreendeudamiento.',
    steps: [
      { icon: '1', text: 'Se verifica si el cliente tiene algun credito en estado SIGNED, DISBURSED, ACTIVE o DELINQUENT' },
      { icon: '2', text: 'Si tiene uno activo → rechazado. Debe liquidarlo o esperar renovacion automatica.' },
    ],
    formula: '¿has_active_credit = NO?',
    examples: {
      pass: 'Sin creditos activos → puede recibir uno nuevo',
      fail: 'Credito ACTIVE vigente → debe liquidar primero',
    },
    configTip: 'Esta regla solo se puede activar o desactivar. La renovacion automatica se ofrece al 75% de avance con score ≥ 61.',
  },
};

// ─── Component ──────────────────────────────────────────────────────────────

export function EligibilitySection({ data, canEdit, onSave, isSaving }: EligibilitySectionProps) {
  const [draft, setDraft] = useState<EligibilityRuleItem[] | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [detailModal, setDetailModal] = useState<string | null>(null);

  const sorted = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => {
      const aNum = parseInt(a.sk.replace('RULE#', ''), 10);
      const bNum = parseInt(b.sk.replace('RULE#', ''), 10);
      return aNum - bNum;
    });
  }, [data]);

  if (sorted.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sin reglas de elegibilidad configuradas.
      </p>
    );
  }

  const current = draft ?? sorted;
  const isDirty = draft !== null;
  const activeDetail = detailModal ? RULE_DETAILS[detailModal] : null;

  function toggleRule(index: number) {
    const base = draft ?? structuredClone(sorted);
    const updated = [...base];
    const rule = updated[index];
    if (rule) {
      updated[index] = { ...rule, is_active: !rule.is_active };
      setDraft(updated);
    }
  }

  function updateValue(index: number, value: string) {
    const base = draft ?? structuredClone(sorted);
    const updated = [...base];
    const rule = updated[index];
    if (rule) {
      updated[index] = { ...rule, condition_value: value };
      setDraft(updated);
    }
  }

  function handleConfirm() {
    if (draft) onSave(draft);
    setShowConfirm(false);
    setDraft(null);
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Reglas eliminatorias evaluadas en orden. Si el cliente no cumple una, se rechaza sin evaluar las siguientes.
      </p>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {current.map((rule, index) => {
          const desc = RULE_DESCRIPTIONS[rule.rule_type] ?? '';
          const threshold = THRESHOLD_CONFIG[rule.rule_type];
          const isEditable = canEdit && EDITABLE_RULES.has(rule.rule_type);
          const hasDetail = Boolean(RULE_DETAILS[rule.rule_type]);
          const priority = rule.sk.replace('RULE#', '');

          return (
            <div
              key={rule.sk}
              className={`rounded-lg border bg-card p-3 transition-opacity border-l-[3px] ${
                rule.is_active ? 'border-l-emerald-500' : 'border-l-muted opacity-40'
              }`}
            >
              {/* Row 1: priority + name + info + knockout + toggle */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-[10px] font-bold text-sky-400">
                    {priority}
                  </span>
                  <span className="text-sm font-semibold text-foreground truncate">{rule.rule_name}</span>
                  {hasDetail && (
                    <button
                      type="button"
                      onClick={() => setDetailModal(rule.rule_type)}
                      className="shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-sky-500/15 hover:text-sky-400"
                      aria-label={`Ver detalle de ${rule.rule_name}`}
                    >
                      <Info size={14} />
                    </button>
                  )}
                  <Badge variant="warning">
                    <ShieldAlert size={10} className="mr-0.5" />
                    Knockout
                  </Badge>
                </div>
                {canEdit ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={rule.is_active}
                    aria-label={`${rule.rule_name}: ${rule.is_active ? 'activa' : 'inactiva'}`}
                    onClick={() => toggleRule(index)}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
                      rule.is_active ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        rule.is_active ? 'translate-x-[18px]' : 'translate-x-[3px]'
                      }`}
                    />
                  </button>
                ) : (
                  <Badge variant={rule.is_active ? 'success' : 'default'}>
                    {rule.is_active ? 'Activa' : 'Inactiva'}
                  </Badge>
                )}
              </div>

              {/* Row 2: description */}
              <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground line-clamp-2">
                {desc}
              </p>

              {/* Row 3: threshold pill OR rejection reason */}
              <div className="mt-2 flex items-center justify-between gap-2">
                {threshold ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-medium text-muted-foreground">{threshold.label}</span>
                    {isEditable ? (
                      <input
                        type="number"
                        value={rule.condition_value}
                        onChange={(e) => updateValue(index, e.target.value)}
                        className="h-7 w-24 rounded-md border border-sky-500/30 bg-sky-500/15 px-2 text-sm font-bold text-sky-400 text-center focus:ring-1 focus:ring-sky-400 focus:outline-none"
                      />
                    ) : (
                      <span className="inline-flex h-7 items-center rounded-md bg-sky-500/15 px-3 text-sm font-bold text-sky-400">
                        {Number(rule.condition_value).toLocaleString('es-MX')}
                      </span>
                    )}
                    <span className="text-[11px] text-muted-foreground">{threshold.unit}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 rounded-md bg-red-500/10 px-2 py-1">
                    <Ban size={12} className="shrink-0 text-red-400" />
                    <span className="text-[11px] font-medium text-red-400">{rule.rejection_reason}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Save bar */}
      {isDirty && (
        <div className="sticky bottom-4 flex items-center justify-end gap-2 rounded-lg border bg-card p-2.5 shadow-lg">
          <span className="mr-auto text-xs text-muted-foreground">Cambios sin guardar</span>
          <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
            Descartar
          </Button>
          <Button variant="primary" size="sm" leftIcon={<Save size={14} />} onClick={() => setShowConfirm(true)}>
            Guardar
          </Button>
        </div>
      )}

      {/* Detail modal */}
      <Modal
        isOpen={activeDetail !== null}
        onClose={() => setDetailModal(null)}
        title={activeDetail?.title ?? ''}
        size="md"
      >
        {activeDetail && (
          <div className="space-y-5">
            {/* Summary */}
            <p className="text-sm text-muted-foreground">{activeDetail.summary}</p>

            {/* Steps as visual flow */}
            <div className="space-y-0">
              {activeDetail.steps.map((step, i) => (
                <div key={i} className="flex gap-3">
                  {/* Vertical line + circle */}
                  <div className="flex flex-col items-center">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-xs font-bold text-sky-400">
                      {step.icon}
                    </span>
                    {i < activeDetail.steps.length - 1 && (
                      <div className="w-px flex-1 bg-border my-0.5" />
                    )}
                  </div>
                  <p className="pb-3 text-sm text-foreground">{step.text}</p>
                </div>
              ))}
            </div>

            {/* Formula highlight */}
            <div className="flex items-center gap-3 rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3">
              <span className="text-lg">⚡</span>
              <span className="text-sm font-bold text-sky-400">{activeDetail.formula}</span>
            </div>

            {/* Examples: pass vs fail */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/20 text-xs">✓</span>
                  <span className="text-xs font-bold text-emerald-400">Aprobado</span>
                </div>
                <p className="text-xs leading-relaxed text-emerald-300/80">{activeDetail.examples.pass}</p>
              </div>
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500/20 text-xs">✗</span>
                  <span className="text-xs font-bold text-red-400">Rechazado</span>
                </div>
                <p className="text-xs leading-relaxed text-red-300/80">{activeDetail.examples.fail}</p>
              </div>
            </div>

            {/* Config tip */}
            {activeDetail.configTip && (
              <div className="rounded-lg bg-muted/50 px-4 py-2.5">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Configuracion:</strong> {activeDetail.configTip}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmSaveModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        isSaving={isSaving}
      />
    </div>
  );
}
